/**
 * Added to Cart tracking. Diffs paid cart lines on cart:updated / cart:build
 * (the queue bypasses both Klaviyo fetch interception and Shopify's Ajax-cart
 * instrumentation). Fires Klaviyo `_learnq` "Added to Cart" (API metric XHJgah,
 * trigger of abandoned cart flow W7YmTQ).
 *
 * r44 (28 Oct 2026): full cart for Klaviyo.
 * 1. Every XHJgah event keeps all earlier keys and adds CartItems (max 20),
 *    CartTotal, ItemCount, Currency, CartRestoreURL. Numbers are JSON numbers.
 * 2. The same snapshot goes to the Klaviyo profile (sc_cart_items, sc_cart_total,
 *    sc_cart_count, sc_cart_currency, sc_cart_restore_url, sc_cart_updated_at)
 *    on every cart change in the page, only when klaviyo.js already knows the
 *    shopper. No new profiles. Empty cart writes [] and 0.
 * 3. /?sc_restore=v1.<base64url> (any page, homepage by default) rebuilds the
 *    paid lines (with line properties) without doubling lines already in the
 *    cart, then opens the MINI CART. Never the /cart page, never checkout.
 *    The reward bar cloth and protection are left to the theme's own rules.
 *    The quiz sign-up gift (_bq_gift) is carried, with its properties, when it
 *    was in the cart and the link has a paid line (r44.2). A bad link just
 *    opens the drawer. /?sc_cart=open opens the drawer with the current cart.
 *    An empty cart's restore link is /?sc_cart=open.
 * r45 (29 Oct 2026): ?sc_discount=CODE (A-Z a-z 0-9, 4 to 32 chars) on any
 *    landing applies an emailed code. Shopify's /discount/CODE?redirect= keeps
 *    only the redirect path, so the restore payload can't ride through it; the
 *    theme calls /discount/CODE itself AFTER the restore adds, then opens the
 *    drawer. Alone on a product page it applies silently (page loads normally).
 *    Shopify decides applicability (3% tier, $50 minimum); no custom messaging.
 *    sc_discount is removed from the address bar; _kx and utm_* stay.
 * r46 (29 Oct 2026): ShortName on every CartItems / sc_cart_items row, from
 *    product.metafields.custom.short_title (Klaviyo's catalog has no
 *    metafields and /cart.js carries none). Cleaned: cut at " | ", spaces
 *    collapsed, max 60 chars, "" when missing (the email falls back to
 *    ProductName). Source: /cart?view=sc-short (product id -> raw short
 *    title, about 3 KB), read only when something is about to be
 *    sent for an identified shopper, cached in sessionStorage, read again only
 *    when an unseen product id is in the cart. Never on page load. ShortName
 *    is left out of the change signature and counts toward the 9.5 KB cap.
 * r46.1: also listens to ajaxProduct:added (PDP and pre-order buttons).
 * r46.2: over the 9.5 KB cap, rows go in this order: protection, then
 *    gifts, then the lowest LinePrice (ties: the later row). What is left
 *    keeps cart order. Was: drop from the end (the oldest lines).
 * No requests on page load beyond the existing cart read. The cart data comes
 * from the queue's own /cart.js read (cart:updated detail), so a cart change
 * costs no extra request in the normal path.
 */
(function () {
  if (window.__SC_KLAVIYO_ATC_INIT__) return;
  window.__SC_KLAVIYO_ATC_INIT__ = true;

  var STORE_ORIGIN = 'https://www.seedcube.com.au';
  var DEBOUNCE_MS = 300;
  var PROFILE_DELAY_MS = 800;
  var MAX_LINES = 20;
  var MAX_PAYLOAD_BYTES = 9500;
  var MAX_RESTORE_CHARS = 3000;
  var RESTORE_PARAM = 'sc_restore';
  var OPEN_PARAM = 'sc_cart';
  var DISCOUNT_PARAM = 'sc_discount';
  var SIG_KEY = 'sc_kl_cart_sig';
  var NAMES_KEY = 'sc_kl_short_v1';
  var NAMES_VIEW = 'sc-short';
  var SHORT_MAX = 60;
  var GIFT_KEYS = ['_sc_threshold_gift', '_shipping_protection'];
  var debounceTimer = null;
  var pendingCartHint = null;
  var paidSnapshot = Object.create(null);
  var pageSig = null;

  /* Trekkie visibility telemetry (22/07/2026): counts Shopify's own
   * "Added Product" analytics events so every custom Added-to-Cart event can
   * report whether Shopify's listener saw the add. Segment TrekkieSawAdd=false
   * in Klaviyo to locate the sessions/pages where Shopify admin ATC tracking
   * still misses real adds. Trekkie's boot stub defines lib.track early and the
   * real trekkie REPLACES lib at load, so we re-wrap on an interval (marker on
   * the function, not the lib) for the first 90s of the page. */
  var trekkieSpyTimer = setInterval(function () {
    try {
      var sa = window.ShopifyAnalytics;
      if (!sa || !sa.lib || typeof sa.lib.track !== 'function') return;
      if (sa.lib.track.__scAtcWrapped) return;
      var origTrack = sa.lib.track;
      var wrapped = function (name) {
        try {
          if (String(name) === 'Added Product') {
            window.__SC_TREKKIE_ATC_COUNT = (window.__SC_TREKKIE_ATC_COUNT || 0) + 1;
          }
        } catch (e) {}
        return origTrack.apply(this, arguments);
      };
      wrapped.__scAtcWrapped = true;
      sa.lib.track = wrapped;
    } catch (e) {}
  }, 750);
  setTimeout(function () { clearInterval(trekkieSpyTimer); }, 90000);

  /* Trigger rule, unchanged: which lines count as a paid add. */
  function isExcludedLine(item) {
    if (!item) return true;
    var props = item.properties || {};
    if (props._sc_threshold_gift === 'true') return true;
    if (props._seedcube_upsell === 'free_gift') return true;
    if (props._shipping_protection === 'true') return true;
    var cents = item.final_price != null ? item.final_price : item.price;
    if (!cents || cents <= 0) return true;
    var name = (item.product_title || item.title || '').trim();
    if (name === 'Shipping Protection') return true;
    if ((item.product_type || '').trim() === 'Insurance') return true;
    return false;
  }

  function buildPaidVariantMap(cart) {
    var map = Object.create(null);
    if (!cart || !cart.items) return map;
    cart.items.forEach(function (item) {
      if (isExcludedLine(item)) return;
      var vid = String(item.variant_id);
      if (!map[vid]) {
        map[vid] = { qty: 0, item: item };
      }
      map[vid].qty += item.quantity;
      map[vid].item = item;
    });
    return map;
  }

  function cartItemsList(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map(function (item) {
      return item.title || item.product_title || String(item.product_id || item.variant_id);
    });
  }

  function productUrl(item) {
    var path = item.url || '';
    if (!path) return STORE_ORIGIN;
    if (path.indexOf('http') === 0) {
      try {
        path = new URL(path).pathname;
      } catch (e) {
        path = '';
      }
    }
    if (path.charAt(0) !== '/') path = '/' + path;
    return STORE_ORIGIN + path;
  }

  function imageUrl(item) {
    var img = (item && item.image) || (item && item.featured_image && item.featured_image.url) || '';
    if (!img) return '';
    if (img.indexOf('//') === 0) img = 'https:' + img;
    return img;
  }

  /* ------------------------------------------------------------ short names */

  /* product id -> cleaned short title ("" = known, none set). */
  var names = (function () {
    try {
      var o = JSON.parse(window.sessionStorage.getItem(NAMES_KEY) || 'null');
      if (o && typeof o === 'object' && !Array.isArray(o)) return o;
    } catch (e) {}
    return Object.create(null);
  })();
  var nameChain = Promise.resolve();
  var nameFailAt = 0;

  /* Cut from " | " (bundle system labels), collapse spaces, max 60 chars. */
  function shortName(v) {
    if (v == null || typeof v === 'object') return '';
    var t = String(v);
    var cut = t.indexOf(' | ');
    if (cut !== -1) t = t.slice(0, cut);
    t = t.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
    if (t.length > SHORT_MAX) t = t.slice(0, SHORT_MAX).trim();
    return t;
  }

  function nameFor(pid) {
    var v = names[String(pid)];
    return typeof v === 'string' ? v : '';
  }

  function fetchNames(missing) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, 4000) : null;
    return window.fetch(cartRoot() + 'cart?view=' + NAMES_VIEW, {
      /* no Accept: application/json here: Shopify then answers with cart JSON
         and ignores the view */
      credentials: 'same-origin',
      signal: ctl ? ctl.signal : undefined
    }).then(function (r) {
      if (!r || !r.ok) throw new Error('names ' + (r && r.status));
      return r.json();
    }).then(function (data) {
      var map = data && data.p;
      if (!map || typeof map !== 'object') throw new Error('names');
      Object.keys(map).forEach(function (id) {
        if (/^\d{1,16}$/.test(id)) names[id] = shortName(map[id]);
      });
      /* ids the view did not list are known as "" so they never re-read */
      missing.forEach(function (id) { if (!(id in names)) names[id] = ''; });
      try { window.sessionStorage.setItem(NAMES_KEY, JSON.stringify(names)); } catch (e) {}
    }).catch(function () {
      nameFailAt = Date.now();
    }).then(function () {
      if (timer) clearTimeout(timer);
    });
  }

  /* One chain, so sends keep their order. Reads only for unseen ids. */
  function ensureNames(pids) {
    nameChain = nameChain.then(function () {
      var missing = pids.filter(function (id) { return !(id in names); });
      if (!missing.length || Date.now() - nameFailAt < 30000) return null;
      return fetchNames(missing);
    });
    return nameChain;
  }

  function applyNames(snap) {
    if (!snap) return snap;
    snap.items.forEach(function (row, i) { row.ShortName = nameFor(snap.pids[i]); });
    return snap;
  }

  /* Only identified shoppers get anything stored, so only they cost a read. */
  function shopperKnown() {
    var kl = window.klaviyo;
    if (!kl || typeof kl.isIdentified !== 'function') return Promise.resolve(true);
    try {
      return Promise.resolve(kl.isIdentified()).then(function (k) { return !!k; }, function () { return true; });
    } catch (e) {
      return Promise.resolve(true);
    }
  }

  /* ---------------------------------------------------------------- snapshot */

  function money(cents) {
    var n = Number(cents);
    return isFinite(n) ? Math.round(n) / 100 : 0;
  }

  function flag(v) {
    return v != null && v !== '' && v !== false && String(v).toLowerCase() !== 'false';
  }

  function lineCents(item) {
    var c = item.final_line_price != null ? item.final_line_price : item.line_price;
    return Number(c) || 0;
  }

  function isProtection(item) {
    var p = item.properties || {};
    if (flag(p._shipping_protection)) return true;
    if (/^shipping protection/i.test(String(item.product_title || item.title || '').trim())) return true;
    return String(item.product_type || '').trim() === 'Insurance';
  }

  function isGift(item) {
    var p = item.properties || {};
    if (flag(p._sc_threshold_gift) || flag(p._bq_gift) || p._seedcube_upsell === 'free_gift') return true;
    return lineCents(item) <= 0;
  }

  /* Quiz sign-up gift (Easy-Scraper Set Free Gift, _bq_gift: signup). Unlike
   * the reward bar cloth, no theme rule re-adds it, so restore links carry it. */
  function isSignupGift(item) {
    return flag((item.properties || {})._bq_gift);
  }

  function image200(item) {
    var img = imageUrl(item);
    if (!img) return '';
    if (/[?&]width=\d+/.test(img)) return img.replace(/([?&])width=\d+/, '$1width=200');
    return img + (img.indexOf('?') === -1 ? '?' : '&') + 'width=200';
  }

  function clean(v, n) {
    return String(v).replace(/[<>"'`\u0000-\u001f\u007f]/g, '').slice(0, n);
  }

  /* Line properties kept on a restored line: no gift or protection markers,
   * strings only, tags and quotes removed, 12 keys max. */
  function cleanProps(p) {
    if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
    var out = {};
    var n = 0;
    Object.keys(p).slice(0, 24).forEach(function (k) {
      if (n >= 12 || GIFT_KEYS.indexOf(k) !== -1) return;
      if (k === '_seedcube_upsell' && p[k] === 'free_gift') return;
      var v = p[k];
      if (v == null || v === '' || typeof v === 'object') return;
      var key = clean(k, 60).trim();
      if (!key) return;
      out[key] = clean(v, 250);
      n += 1;
    });
    return n ? out : null;
  }

  function lineKey(id, props) {
    var p = props || {};
    return String(id) + '|' + Object.keys(p).sort().map(function (k) { return k + '=' + p[k]; }).join('&');
  }

  function b64e(str) {
    var bytes = new TextEncoder().encode(str);
    var bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function b64d(s) {
    s = String(s).replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    var bin = atob(s);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  }

  function cartRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function restoreUrl(rows) {
    var base = STORE_ORIGIN + cartRoot();
    var open = base + '?' + OPEN_PARAM + '=open';
    if (!rows.length) return open;
    var url = '';
    for (var n = rows.length; n > 0; n--) {
      try {
        url = base + '?' + RESTORE_PARAM + '=v1.' + b64e(JSON.stringify(rows.slice(0, n)));
      } catch (e) {
        return open;
      }
      if (url.length <= MAX_RESTORE_CHARS) return url;
    }
    return open;
  }

  function byteSize(o) {
    try {
      return new TextEncoder().encode(JSON.stringify(o)).length;
    } catch (e) {
      return 0;
    }
  }

  function snapshot(cart) {
    var items = [];
    var pids = [];
    var rows = [];
    var paidUnits = 0;
    var protCents = 0;
    var sumCents = 0;
    (cart.items || []).forEach(function (item) {
      var prot = isProtection(item);
      var gift = !prot && isGift(item);
      var qty = Number(item.quantity) || 0;
      var lc = lineCents(item);
      sumCents += lc;
      if (prot) {
        protCents += lc;
      } else if (!gift || isSignupGift(item)) {
        /* paid lines, plus the quiz sign-up gift (kept with its _bq_gift
           property); the reward bar cloth is left to the theme */
        if (!gift) paidUnits += qty;
        if (rows.length < MAX_LINES) {
          var row = [Number(item.variant_id), qty];
          var props = cleanProps(item.properties);
          var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id;
          if (props || plan) row.push(props || 0);
          if (plan) row.push(Number(plan));
          rows.push(row);
        }
      }
      if (items.length >= MAX_LINES) return;
      var vt = item.variant_title || '';
      if (vt === 'Default Title') vt = '';
      pids.push(String(item.product_id || ''));
      items.push({
        ProductName: item.product_title || item.title || '',
        ShortName: nameFor(item.product_id),
        VariantTitle: vt,
        Quantity: qty,
        LinePrice: money(lc),
        UnitPrice: money(item.final_price != null ? item.final_price : item.price),
        ImageURL: image200(item),
        ProductURL: productUrl(item),
        VariantID: String(item.variant_id),
        SKU: item.sku || '',
        IsGift: gift,
        IsProtection: prot
      });
    });
    var subtotal = cart.items_subtotal_price != null ? Number(cart.items_subtotal_price) : sumCents;
    var snap = {
      items: items,
      pids: pids,
      total: money(Math.max(0, subtotal - protCents)),
      count: paidUnits,
      currency: String(cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD'),
      restoreUrl: restoreUrl(rows)
    };
    /* ShortName stays out of the signature: a cart only counts as changed
       when its lines, total, count or currency change */
    snap.sig = JSON.stringify([snap.items, snap.total, snap.count, snap.currency], function (k, v) {
      return k === 'ShortName' ? undefined : v;
    });
    return snap;
  }

  /* r46.2: which row goes first when over budget. Protection, then gifts
     (later row first), then the lowest LinePrice (ties: the later row). */
  function dropIndex(rows) {
    var pick = -1;
    var rank = -1;
    var price = Infinity;
    rows.forEach(function (r, i) {
      var k = r.IsProtection ? 2 : (r.IsGift ? 1 : 0);
      var lp = Number(r.LinePrice) || 0;
      if (k > rank || (k === rank && (k > 0 || lp <= price))) {
        pick = i;
        rank = k;
        price = lp;
      }
    });
    return pick;
  }

  /* Drop rows until the object fits the byte budget; the rest keep cart order. */
  function fit(props, listKey) {
    props[listKey] = props[listKey].slice();
    while (props[listKey].length && byteSize(props) > MAX_PAYLOAD_BYTES) {
      props[listKey].splice(dropIndex(props[listKey]), 1);
    }
    return props;
  }

  /* ------------------------------------------------------------------ event */

  function trackPaidLine(item, cart, snap) {
    if (!window._learnq) return;
    var cents = item.final_price != null ? item.final_price : item.price;
    var dollars = cents / 100;
    var name = item.product_title || item.title || '';
    var props = {
      '$value': dollars,
      'Product Name': name,
      'Price': dollars,
      'Product Type': item.product_type || '',
      'ProductURL': productUrl(item),
      'ImageURL': imageUrl(item),
      'items': cartItemsList(cart),
      'TrekkieSawAdd': (window.__SC_TREKKIE_ATC_COUNT || 0) > 0,
      'TrekkieATCCount': window.__SC_TREKKIE_ATC_COUNT || 0,
      'PagePath': window.location.pathname
    };
    if (snap) {
      props.CartItems = snap.items.slice();
      props.CartTotal = snap.total;
      props.ItemCount = snap.count;
      props.Currency = snap.currency;
      props.CartRestoreURL = snap.restoreUrl;
      fit(props, 'CartItems');
    }
    window._learnq.push(['track', 'Added to Cart', props]);
  }

  /* ---------------------------------------------------------------- profile */

  var profileTimer = null;
  var pendingSnap = null;

  function store(op, val) {
    try {
      if (op === 'get') return window.localStorage.getItem(SIG_KEY);
      window.localStorage.setItem(SIG_KEY, val);
    } catch (e) {}
    return null;
  }

  function profileProps(snap) {
    return fit({
      sc_cart_items: snap.items.slice(),
      sc_cart_total: snap.total,
      sc_cart_count: snap.count,
      sc_cart_currency: snap.currency,
      sc_cart_restore_url: snap.restoreUrl,
      sc_cart_updated_at: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z')
    }, 'sc_cart_items');
  }

  function writeProfile() {
    clearTimeout(profileTimer);
    profileTimer = null;
    var snap = pendingSnap;
    pendingSnap = null;
    if (!snap || snap.sig === store('get')) return;
    var kl = window.klaviyo;
    if (!kl || typeof kl.isIdentified !== 'function' || typeof kl.identify !== 'function') return;
    try {
      Promise.resolve(kl.isIdentified()).then(function (known) {
        if (!known) return null;
        return Promise.resolve(kl.identify(profileProps(snap))).then(function () {
          store('set', snap.sig);
        });
      }).catch(function () {});
    } catch (e) {}
  }

  function queueProfile(snap) {
    pendingSnap = snap;
    clearTimeout(profileTimer);
    profileTimer = setTimeout(writeProfile, PROFILE_DELAY_MS);
  }

  /* --------------------------------------------------------------- the diff */

  function resolveCart(hint) {
    if (hint && window.SC_isValidCart && window.SC_isValidCart(hint)) {
      return Promise.resolve(hint);
    }
    if (window.SC_getSharedCart) {
      return window.SC_getSharedCart({ forceFresh: true });
    }
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) {
      return res.json();
    });
  }

  function initSnapshot() {
    resolveCart(null).then(function (cart) {
      if (!cart || !cart.items) return;
      paidSnapshot = buildPaidVariantMap(cart);
      try { pageSig = snapshot(cart).sig; } catch (e) {}
    }).catch(function () {});
  }

  function flushDiff() {
    var hint = pendingCartHint;
    pendingCartHint = null;
    /* a read that starts during a restore may return a half-built cart:
       ignore it completely; the restore sets the baseline when it is done */
    if (window.__SC_CART_RESTORING__) return;

    resolveCart(hint).then(function (cart) {
      if (!cart || !cart.items || window.__SC_CART_RESTORING__) return;

      var snap = null;
      try { snap = snapshot(cart); } catch (e) { snap = null; }

      var afterMap = buildPaidVariantMap(cart);
      var increases = findPaidIncreases(paidSnapshot, afterMap);
      var track = increases.length && window._learnq ? increases : [];
      paidSnapshot = afterMap;

      var profile = !!(snap && snap.sig !== pageSig);
      if (profile) pageSig = snap.sig;
      if (!track.length && !profile) return;

      /* r46: short titles first. The baseline above is already updated, so a
         second change during the read can't fire the same add twice. */
      var job = { cart: cart, snap: snap, track: track, profile: profile };
      waiting.push(job);
      var needRead = snap && (track.length || snap.sig !== store('get'));
      (needRead ? shopperKnown() : Promise.resolve(false)).then(function (known) {
        return known ? ensureNames(snap.pids) : nameChain;
      }).catch(function () {}).then(function () {
        sendJob(job);
      });
    }).catch(function () {});
  }

  var waiting = [];

  function sendJob(job) {
    var i = waiting.indexOf(job);
    if (i === -1) return;
    waiting.splice(i, 1);
    applyNames(job.snap);
    if (!window.__SC_CART_RESTORING__) {
      job.track.forEach(function (line) {
        trackPaidLine(line, job.cart, job.snap);
      });
    }
    if (job.profile) queueProfile(job.snap);
  }

  function findPaidIncreases(beforeMap, afterMap) {
    var lines = [];
    Object.keys(afterMap).forEach(function (vid) {
      var was = beforeMap[vid] ? beforeMap[vid].qty : 0;
      var now = afterMap[vid].qty;
      if (now > was) lines.push(afterMap[vid].item);
    });
    return lines;
  }

  function scheduleDiff(evt) {
    if (evt && evt.detail && evt.detail.cart) {
      pendingCartHint = evt.detail.cart;
    }
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flushDiff, DEBOUNCE_MS);
  }

  document.addEventListener('cart:updated', scheduleDiff);
  document.addEventListener('cart:build', scheduleDiff);
  /* r46.1: the pre-order button only fires ajaxProduct:added (no cart:updated
     or cart:build), so its adds never reached Klaviyo. The diff can't double
     count, and a cart:updated in the same 300 ms supplies its cart. */
  document.addEventListener('ajaxProduct:added', scheduleDiff);

  /* A change just before leaving the page: send it now. */
  window.addEventListener('pagehide', function () {
    waiting.slice().forEach(sendJob);
    if (profileTimer) writeProfile();
  });

  /* Email given in a Klaviyo form after items went in: copy the cart to the
   * new profile once (skipped for an empty cart with nothing written before). */
  window.addEventListener('klaviyoForms', function (e) {
    if (!e || !e.detail || e.detail.type !== 'submit') return;
    setTimeout(function () {
      resolveCart(window.SC_getLastCart ? window.SC_getLastCart(60000) : null).then(function (cart) {
        if (!cart || !cart.items) return;
        if (!cart.items.length && !store('get')) return;
        var snap = snapshot(cart);
        return ensureNames(snap.pids).then(function () {
          pendingSnap = applyNames(snap);
          writeProfile();
        });
      }).catch(function () {});
    }, 2500);
  });

  /* ---------------------------------------------------------------- restore */

  function parseRestore(code) {
    if (typeof code !== 'string' || code.length > 4000) return null;
    var m = /^v1\.([A-Za-z0-9_-]+)$/.exec(code);
    if (!m) return null;
    var data;
    try { data = JSON.parse(b64d(m[1])); } catch (e) { return null; }
    if (!Array.isArray(data) || !data.length) return null;
    var merged = Object.create(null);
    var order = [];
    data.slice(0, MAX_LINES).forEach(function (r) {
      if (!Array.isArray(r)) return;
      var id = Number(r[0]);
      var qty = Number(r[1]);
      if (!(id > 0 && id < 1e16 && Math.floor(id) === id)) return;
      if (!(qty >= 1 && qty <= 99 && Math.floor(qty) === qty)) return;
      var props = cleanProps(r[2]);
      var plan = Number(r[3]);
      var key = lineKey(id, props);
      if (merged[key]) {
        merged[key].quantity = Math.min(99, merged[key].quantity + qty);
        return;
      }
      var line = { id: id, quantity: qty };
      if (props) line.properties = props;
      if (plan > 0 && Math.floor(plan) === plan) line.selling_plan = plan;
      merged[key] = line;
      order.push(key);
    });
    var lines = order.map(function (k) { return merged[k]; });
    /* a sign-up gift only travels with at least one paid line, max 1 */
    var paid = lines.filter(function (l) { return !(l.properties && flag(l.properties._bq_gift)); });
    if (!paid.length) return null;
    var gifts = lines.filter(function (l) { return l.properties && flag(l.properties._bq_gift); }).slice(0, 1);
    gifts.forEach(function (g) { g.quantity = 1; });
    return paid.concat(gifts);
  }

  /* Remove our params from the address bar; every other param (_kx, utm_*,
   * discount, preview) stays so klaviyo.js and analytics still read them. */
  function stripParams() {
    try {
      var p = new URLSearchParams(window.location.search);
      if (!p.has(RESTORE_PARAM) && !p.has(OPEN_PARAM) && !p.has(DISCOUNT_PARAM)) return;
      p.delete(RESTORE_PARAM);
      p.delete(OPEN_PARAM);
      p.delete(DISCOUNT_PARAM);
      var q = p.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash);
    } catch (e) {}
  }

  /* Same open path as the quiz: rebuild, then open the mini cart. Waits for
   * theme.js (deferred, bound on DOMContentLoaded) to be listening. */
  function whenReady(fn) {
    function go() { setTimeout(fn, 60); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
    else go();
  }

  function openDrawer() {
    try {
      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (e) {}
  }

  function addLines(lines) {
    function post(body) {
      return window.fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
        SC_ORIGIN: 'system'
      }).then(function (r) { return !!(r && r.ok); }, function () { return false; });
    }
    return post({ items: lines }).then(function (ok) {
      if (ok || lines.length < 2) return;
      /* one line sold out or gone: add the rest one by one */
      return lines.reduce(function (chain, line) {
        return chain.then(function () { return post(line); });
      }, Promise.resolve());
    });
  }

  function freshCart() {
    if (window.SC_getSharedCart) return window.SC_getSharedCart({ forceFresh: true });
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  /* r45: emailed discount code. Letters and digits only, 4 to 32 chars;
   * anything else is ignored. */
  function readDiscountCode(p) {
    var c = p.get(DISCOUNT_PARAM);
    if (c == null) return null;
    c = String(c).trim();
    return /^[A-Za-z0-9]{4,32}$/.test(c) ? c : null;
  }

  /* /discount/CODE sets the code on the cart. Its redirect is pointed at
   * cart.js so the response is the updated cart (about 5 KB) instead of the
   * homepage HTML (about 890 KB). The cart goes into the queue's cache so the
   * drawer renders the discount line without another read. */
  function applyDiscount(code) {
    var root = cartRoot();
    return window.fetch(root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart.js'), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      return r && r.ok ? r.json() : null;
    }).catch(function () { return null; }).then(function (cart) {
      if (cart && cart.items) {
        if (window.SC_setLastCart) window.SC_setLastCart(cart);
        return cart;
      }
      return freshCart();
    });
  }

  function onProductPage() {
    return /\/products\/[^/]+/.test(window.location.pathname);
  }

  /* Returns true while a restore owns the cart (tracking baseline set later). */
  function handleLinkParams() {
    var p;
    try { p = new URLSearchParams(window.location.search); } catch (e) { return false; }
    var code = p.get(RESTORE_PARAM);
    var open = p.get(OPEN_PARAM) === 'open';
    var discount = readDiscountCode(p);
    if (code == null && !open && !discount && !p.has(DISCOUNT_PARAM)) return false;
    stripParams();
    var wanted = code == null ? null : parseRestore(code);
    if (!wanted) {
      if (!discount) {
        /* open param, or a bad / expired link: drawer with the current cart */
        if (code != null || open) whenReady(openDrawer);
        return false;
      }
      /* r45: code with sc_cart=open, on its own, or with a bad restore link.
       * A product page applies it silently; elsewhere the drawer opens when
       * the link asked for it or the cart has items. */
      var linkOpens = code != null || open;
      applyDiscount(discount).then(function (cart) {
        whenReady(function () {
          if (linkOpens || (!onProductPage() && cart && cart.item_count > 0)) openDrawer();
          else if (window.SC_emitCartBuild) window.SC_emitCartBuild();
        });
      });
      return false;
    }
    window.__SC_CART_RESTORING__ = true;
    whenReady(openDrawer);
    freshCart().then(function (cart) {
      var have = Object.create(null);
      ((cart && cart.items) || []).forEach(function (item) {
        /* sign-up gift lines count too, so a second open never doubles it */
        if (isProtection(item) || (isGift(item) && !isSignupGift(item))) return;
        var k = lineKey(item.variant_id, cleanProps(item.properties));
        have[k] = (have[k] || 0) + (Number(item.quantity) || 0);
      });
      var adds = [];
      wanted.forEach(function (line) {
        var k = lineKey(line.id, line.properties);
        var need = line.quantity - (have[k] || 0);
        if (need > 0) adds.push(Object.assign({}, line, { quantity: need }));
      });
      return adds.length ? addLines(adds) : null;
    }).catch(function () {}).then(function () {
      /* r45: the code goes on after the lines, so a $50 minimum sees them */
      return discount ? applyDiscount(discount) : freshCart();
    }).then(function (cart) {
      if (cart && cart.items) {
        paidSnapshot = buildPaidVariantMap(cart);
        try { pageSig = snapshot(cart).sig; } catch (e) {}
      }
    }).catch(function () {}).then(function () {
      window.__SC_CART_RESTORING__ = false;
      whenReady(function () {
        /* gift reconcile (reward bar cloth) + drawer re-render with the new lines */
        try { document.dispatchEvent(new CustomEvent('ajaxProduct:added')); } catch (e) {}
        if (window.SC_emitCartBuild) window.SC_emitCartBuild();
        openDrawer();
      });
    });
    return true;
  }

  var restoring = false;
  try { restoring = handleLinkParams(); } catch (e) { restoring = false; }

  if (!restoring) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSnapshot);
    } else {
      initSnapshot();
    }
  }
})();
